import { useCallback } from 'react'

import browserLocationHook from 'wouter/use-location'

const splitOnce = (value, separator) => {
  const index = value.indexOf(separator)

  return index === -1 ? [value, ''] : [value.slice(0, index), value.slice(index + 1)]
}

/**
 * Merges the query string of the current URL into a navigation target.
 *
 * Params the target declares itself win over the carried-over ones.
 *
 * @param {string} to wouter navigation target, e.g. `/payment`, `~/checkout-confirmation?data=…`
 * @return {string} the target with the current query string merged in
 */
export const preserveCurrentQuery = (to) => {
  const currentSearch = typeof window === 'undefined' ? '' : window.location.search
  if (!currentSearch) return to

  const [pathWithSearch, hash] = splitOnce(to, '#')
  const [path, search] = splitOnce(pathWithSearch, '?')

  const params = new URLSearchParams(currentSearch)
  const targetParams = new URLSearchParams(search)

  new Set(targetParams.keys()).forEach((key) => params.delete(key))
  targetParams.forEach((value, key) => params.append(key, value))

  return `${path}?${params}${hash ? `#${hash}` : ''}`
}

/**
 * Wouter location hook that carries the current query string over to every client-side navigation.
 *
 * @param {Object} [options] options forwarded to wouter's browser location hook, e.g. `{ base }`
 * @return {[string, Function]} the current path and a query-preserving navigate function
 */
const useQueryPreservingLocation = (options) => {
  const [path, navigate] = browserLocationHook(options)

  const navigateWithQuery = useCallback(
    (to, navigateOptions) => navigate(preserveCurrentQuery(to), navigateOptions),
    [navigate],
  )

  return [path, navigateWithQuery]
}

export default useQueryPreservingLocation
